import { observable, action, makeObservable, computed } from 'mobx';
import { PAYMENT_METHODS, EPAY_CARD_PREFIX } from '../constants';
import { countryRegions } from '../constants/country';
import { trace } from '../utils/notifyParent';
import { COUPON_INVALID_TYPES, EPAY_ERROR_CODES, TRACING_EVENTS } from '../../shared/constants';
import {
	InitializationError,
	InternalError,
	MerchantWalletError,
	PaymentMethodVerificationError,
} from '../utils/errors';
import { request } from '../services/api';
import { createUUID } from '../utils/createUUID';
import { isPaymentMethodAvailable, TOKENIZATION_PAYMENT_METHODS } from '../constants/paymentMethod';
import { MerchantWalletMapper } from '../mappers/merchantWalletMapper';

const setNullIfEmptyString = (object, property) => {
	const value = object[property];
	if (typeof value === 'string' && !value.trim()) {
		object[property] = null;
	}
};

const setNullIfNaN = (object, property) => {
	const value = object[property];
	object[property] = +value || null;
};

class Customer {
	isLoading = false;
	billingDetails = null;
	priceDetails = null;
	subscriptionId = null;
	isEdited = false;
	paymentMethodType = null;
	persistedPaymentMethodType = null;
	paymentMethod = {};
	paymentDetails = null;
	availablePaymentMethods = {};
	embedderAvailablePaymentMethods = {};
	coupon = null;
	couponCode = '';
	invalidCouponCode = null;
	planName = '';
	customerReference = '';
	hasPaymentMethod = false;
	tokenizationId = null;
	merchantWalletActivePaymentMethods = [];
	merchantWalletAvailablePaymentMethods = null;

	constructor({ sumupService, epayService, sumupSubscriptionsService, merchantWalletService }) {
		makeObservable(this, {
			isLoading: observable,
			billingDetails: observable,
			priceDetails: observable,
			subscriptionId: observable,
			isEdited: observable,
			paymentMethodType: observable,
			persistedPaymentMethodType: observable,
			isPersistedPaymentMethodAvailable: computed,
			availablePaymentMethodsForEdit: computed,
			paymentMethod: observable,
			paymentDetails: observable,
			availablePaymentMethods: observable,
			embedderAvailablePaymentMethods: observable,
			merchantWalletActivePaymentMethods: observable,
			merchantWalletAvailablePaymentMethods: observable,
			coupon: observable,
			couponCode: observable,
			invalidCouponCode: observable,
			planName: observable,
			customerReference: observable,
			hasPaymentMethod: observable,
			setBillingDetails: action,
			setPaymentMethod: action,
			setLegalEntities: action,
			fetchCustomerDetails: action,
			getPersistedPaymentMethodForPurchase: action,
			prepareSubscriptionPurchase: action,
			preparePaymentMethodUpdate: action,
			verifyPaymentMethod: action,
			updateBillingDetails: action,
			updateVatNumber: action,
			setVatNumber: action,
			setCouponCode: action,
			purchaseSubscription: action,
			updatePaymentMethod: action,
			setAvailablePaymentMethods: action,
			setIsEdited: action,
			setPlanName: action,
			setCustomerReference: action,
			mergeEmbedderPaymentMethodAvailability: action,
			initializePaymentMethodAvailability: action,
		});

		if (!sumupService || !epayService || !sumupSubscriptionsService || !merchantWalletService) {
			throw new InitializationError('sumupService, epayService, sumupSubscriptionsService, and merchantWalletService should be provided');
		}
		this.sumupService = sumupService;
		this.epayService = epayService;
		this.sumupSubscriptionsService = sumupSubscriptionsService;
		this.merchantWalletService = merchantWalletService;
	}

	// PUBLIC AND PRIVATE ASYNC METHODS //
	async prepareSubscriptionPurchase({ redirectUrl } = {}) {
		await this._upsertMerchantWalletCustomer();

		const requiresTokenization = TOKENIZATION_PAYMENT_METHODS.includes(this.paymentMethodType);

		if (requiresTokenization) {
			return this._prepareTokenization({ redirectUrl });
		}

		if (this.paymentMethodType === PAYMENT_METHODS.suPayout) {
			return this.preparePayoutPM();
		}

		if (this.paymentMethodType === PAYMENT_METHODS.suPaymentMethods) {
			return this.prepareSuPaymentMethodPM();
		}

		if (this.paymentMethodType === PAYMENT_METHODS.suBusinessAccount) {
			return this.prepareSuBusinessAccountPM();
		}

		throw new InternalError('Unsupported payment method for subscription purchase', {
			paymentMethodType: this.paymentMethodType,
		});
	}

	async purchaseSubscription({ planId, paymentMethodData, promotionId, discountId }) {
		const { type, creditCard, directDebit, businessAccount, suPayout, suPaymentMethods, suBusinessAccount } = paymentMethodData;
		const { couponCode } = this;
		const paymentMethodPayload = Object.assign({}, creditCard || directDebit || businessAccount || suPayout || suPaymentMethods || suBusinessAccount);

		if (type === PAYMENT_METHODS.creditCard && !this.hasPaymentMethod) { // existing payment method already has everything set
			const { expiryMonth, expiryYear, cardholderName } = this.paymentMethod;
			const expiry = `${expiryMonth}/${expiryYear}`;

			Object.assign(paymentMethodPayload, { expiry, cardholderName });
		}

		const purchaseRequest = {
			paymentMethodType: type,
			paymentMethodData: paymentMethodPayload,
			planId,
			couponCode,
			customerBillingDetails: this.billingDetails,
			customerReference: this.customerReference,
			purchaseReference: createUUID(),
			promotionId,
			discountId,
		};
		let subscriptionPurchaseData;

		try {
			subscriptionPurchaseData = await this.sumupSubscriptionsService.purchaseSubscription(purchaseRequest);
		} catch (err) {
			trace(TRACING_EVENTS.PURCHASE_FAILED, { reason: err.message });

			throw err;
		}

		const { charge, paymentMethod, subscriptionId } = subscriptionPurchaseData;

		this.subscriptionId = subscriptionId;

		trace(TRACING_EVENTS.SUBSCRIPTION_PURCHASED, { subscriptionId });
		return {
			...this._extractPaymentMethodDetails(paymentMethod),
			...this._extractChargeDetails(charge),
		};
	}

	async preparePaymentMethodUpdate({ redirectUrl }) {
		const requiresTokenization = TOKENIZATION_PAYMENT_METHODS.includes(this.paymentMethodType);

		if (requiresTokenization) {
			return this._prepareTokenization({ redirectUrl });
		}

		if (this.paymentMethodType === PAYMENT_METHODS.suPayout) {
			return this.preparePayoutPM();
		}

		if (this.paymentMethodType === PAYMENT_METHODS.suPaymentMethods) {
			return this.prepareSuPaymentMethodPM();
		}

		if (this.paymentMethodType === PAYMENT_METHODS.suBusinessAccount) {
			return this.prepareSuBusinessAccountPM();
		}

		throw new InternalError('Unsupported payment method for payment method update', {
			paymentMethodType: this.paymentMethodType,
		});
	}

	async updatePaymentMethod(paymentMethodData) {
		const { type, creditCard, directDebit, suPayout, suPaymentMethods, suBusinessAccount } = paymentMethodData;
		const paymentMethodPayload = creditCard || directDebit || suPayout || suPaymentMethods || suBusinessAccount;

		if (type === PAYMENT_METHODS.creditCard) {
			const { expiryMonth, expiryYear, cardholderName } = this.paymentMethod;
			const expiry = `${expiryMonth}/${expiryYear}`;

			Object.assign(paymentMethodPayload, { expiry, cardholderName });
		}

		const requiresTokenization = TOKENIZATION_PAYMENT_METHODS.includes(type);

		if (!requiresTokenization) {
			this.paymentMethodType = type;

			try {
				await this._setDefaultMerchantWalletPaymentMethod(type);
			} catch (err) {
				trace(TRACING_EVENTS.PAYMENT_METHOD_UPDATE_FAILED, { reason: err.message });
				throw err;
			}
		}

		trace(TRACING_EVENTS.PAYMENT_METHOD_UPDATED);

		this.paymentMethodType = type;
		this.paymentDetails = paymentMethodPayload;
		this.persistedPaymentMethodType = type;

		return this._extractPaymentMethodDetails({
			[type]: paymentMethodPayload,
		});
	}

	async verifyPaymentMethod({ checkoutId }) {
		if (!this.tokenizationId) {
			throw new InternalError('Payment method verification requires tokenization', {
				paymentMethodType: this.paymentMethodType,
			});
		}

		return this._confirmMerchantWalletTokenization({ checkoutId });
	}

	async updateBillingDetails() {
		const billingDetails = { ...this.billingDetails };

		delete billingDetails.vatNumber;
		setNullIfEmptyString(billingDetails, 'phone');
		setNullIfEmptyString(billingDetails, 'state');
		setNullIfEmptyString(billingDetails, 'addressLine2');
		delete billingDetails.invoiceInfo;
		delete billingDetails.regionId;

		try {
			await this.merchantWalletService.patchBillingAddress({ billingAddress: billingDetails });
			trace(TRACING_EVENTS.BILLING_DETAILS_UPDATED);
		} catch (err) {
			trace(TRACING_EVENTS.BILLING_DETAILS_UPDATE_FAILED, { reason: err.message });

			throw err;
		}

		return { ...this.billingDetails }; // remove proxy
	}

	async updateVatNumber() {
		try {
			await this.merchantWalletService.patchBillingAddress({
				vatNumber: this.billingDetails.vatNumber,
			});
			trace(TRACING_EVENTS.BILLING_DETAILS_UPDATED);
		} catch (err) {
			trace(TRACING_EVENTS.BILLING_DETAILS_UPDATE_FAILED, { reason: err.message });

			throw err;
		}


		return this.billingDetails.vatNumber;
	}

	mergeEmbedderPaymentMethodAvailability(embedderAvailablePaymentMethods) {
		// Snapshot embedder intent; used when merged availability hides a type MW still keeps active.
		this.embedderAvailablePaymentMethods = { ...embedderAvailablePaymentMethods };

		if (this.merchantWalletAvailablePaymentMethods) {
			this.setAvailablePaymentMethods(
				MerchantWalletMapper.mergeAvailablePaymentMethods(
					embedderAvailablePaymentMethods,
					this.merchantWalletAvailablePaymentMethods,
					this.merchantWalletActivePaymentMethods,
				),
			);
			return;
		}

		this.setAvailablePaymentMethods(
			MerchantWalletMapper.mapEmbedderPaymentMethodsToTokenizedOnlyAvailability(embedderAvailablePaymentMethods),
		);
	}

	async initializePaymentMethodAvailability(embedderAvailablePaymentMethods, { countryCode } = {}) {
		try {
			const available = await this._fetchMerchantWalletPaymentMethodAvailability({ countryCode });

			if (!available) {
				this.merchantWalletAvailablePaymentMethods = null;
				this.merchantWalletActivePaymentMethods = [];
				this.mergeEmbedderPaymentMethodAvailability(embedderAvailablePaymentMethods);
				return;
			}

			this.merchantWalletAvailablePaymentMethods = available.availablePaymentMethods;
			this.merchantWalletActivePaymentMethods = available.active || [];
			this.mergeEmbedderPaymentMethodAvailability(embedderAvailablePaymentMethods);
		} catch (err) {
			this.merchantWalletAvailablePaymentMethods = null;
			this.merchantWalletActivePaymentMethods = [];
			this.mergeEmbedderPaymentMethodAvailability(embedderAvailablePaymentMethods);
		}
	}

	async _fetchMerchantWalletPaymentMethodAvailability({ countryCode }) {
		const customerAvailable = await this.merchantWalletService.getAvailablePaymentMethods();
		if (customerAvailable) {
			return customerAvailable;
		}

		if (!countryCode) {
			return null;
		}

		return this.merchantWalletService.getMerchantAvailablePaymentMethods({ countryCode });
	}

	async fetchCustomerDetails() {
		let customerDetails;

		try {
			customerDetails = await this.merchantWalletService.getCustomer();
		} catch (err) {
			if (err instanceof MerchantWalletError && err.isHTTPNotFoundError()) {
				return null;
			}

			throw err;
		}

		if (!customerDetails || Object.keys(customerDetails).length === 0) {
			return null;
		}

		const defaultPaymentMethod = {
			type: PAYMENT_METHODS.creditCard,
			creditCard: { type: '', last4: '' },
		};
		const {
			billingDetails,
			hasPaymentMethod,
		} = customerDetails;
		// `paymentMethod` may be explicitly `null` in the API response, in which case a
		// destructuring default value would not apply, so we fall back here instead.
		this.persistedPaymentMethodType = customerDetails.paymentMethod && customerDetails.paymentMethod.type || null;
		const paymentMethod = customerDetails.paymentMethod || defaultPaymentMethod;
		const { type, creditCard, directDebit, suPayout, suPaymentMethods, suBusinessAccount } = paymentMethod;
		this.checkPaymentMethod(type);

		this.paymentMethodType = type;
		this.hasPaymentMethod = hasPaymentMethod;
		if (billingDetails) {
			this.setBillingDetails(billingDetails);
		}

		switch (type) {
			case PAYMENT_METHODS.creditCard:
				return this.paymentDetails = creditCard;
			case PAYMENT_METHODS.directDebit:
				return this.paymentDetails = directDebit;
			case PAYMENT_METHODS.suPayout:
				return this.paymentDetails = suPayout;
			case PAYMENT_METHODS.suPaymentMethods:
				return this.paymentDetails = suPaymentMethods;
			case PAYMENT_METHODS.suBusinessAccount:
				return this.paymentDetails = suBusinessAccount;
			default:
				return;
		}
	}

	async getPersistedPaymentMethodForPurchase() {
		const type = this.persistedPaymentMethodType;
		let paymentDetails = this.paymentDetails;

		const missingCreditCardToken = type === PAYMENT_METHODS.creditCard && !paymentDetails?.token;
		const missingDirectDebitMandate = type === PAYMENT_METHODS.directDebit && !paymentDetails?.mandateReference;

		if (missingCreditCardToken || missingDirectDebitMandate) {
			const merchantWalletCustomer = await this.merchantWalletService.getCustomer();
			const { paymentMethod } = merchantWalletCustomer || {};

			if (paymentMethod?.type === type) {
				paymentDetails = paymentMethod.creditCard
					|| paymentMethod.directDebit
					|| paymentDetails;
			}

			const stillMissingCreditCardToken = type === PAYMENT_METHODS.creditCard && !paymentDetails?.token;
			const stillMissingDirectDebitMandate = type === PAYMENT_METHODS.directDebit && !paymentDetails?.mandateReference;

			if (stillMissingCreditCardToken || stillMissingDirectDebitMandate) {
				throw new InternalError('Persisted payment method details are not available for purchase');
			}

			this.paymentDetails = paymentDetails;
		}

		return {
			type,
			[type]: paymentDetails,
		};
	}

	async setCouponCode({ couponCode, planId }) {
		if (this.isLoading) {
			return;
		}
		// coupon restarted
		if (!couponCode) {
			this.couponCode = '';
			this.coupon = null;
			return;
		}

		this.isLoading = true;

		try {
			if (couponCode.startsWith(EPAY_CARD_PREFIX)) {
				try {
					const coupon = await this.epayService.validateCard({ couponCode, planId });
					if (coupon.valid) {
						this.couponCode = coupon.coupon;
						trace(TRACING_EVENTS.EPAY_CARD_VERIFIED, { couponCode });
						this.coupon = coupon;
						this.invalidCouponCode = null;
						return;
					}

					trace(TRACING_EVENTS.EPAY_CARD_VERIFICATION_FAILED, { couponCode });
				} catch (error) {
					trace(TRACING_EVENTS.EPAY_CARD_VERIFICATION_FAILED, { couponCode });
					if (error.errorCode === EPAY_ERROR_CODES.UNSUPPORTED_FREQUENCY) {
						this.couponCode = '';
						this.coupon = { valid: false };
						this.invalidCouponCode = COUPON_INVALID_TYPES.UNSUPPORTED_FREQUENCY;
						return;
					}
				}
			}

			this.couponCode = '';
			this.coupon = { valid: false };
			this.invalidCouponCode = COUPON_INVALID_TYPES.INVALID_CODE;
		} finally {
			this.isLoading = false;
		}
	}

	async getUserMandate() {
		const userAgent = window.navigator.userAgent;
		const { userIp } = await request.get('/ip');
		const type = 'recurrent';

		return {
			user_agent: userAgent,
			user_ip: userIp,
			type
		};
	}

	// SETTERS //
	setPaymentMethod(paymentMethodType, paymentMethod) {
		this.checkPaymentMethod(paymentMethodType);

		this.paymentMethodType = paymentMethodType;

		switch (paymentMethodType) {
			case PAYMENT_METHODS.creditCard: {
				const {
					cardholderName,
					cardnumber,
					expiryMonth,
					expiryYear,
					cvv,
					zipCode
				} = paymentMethod;
				this.paymentMethod = {
					cardholderName,
					cardnumber,
					expiryMonth,
					expiryYear,
					cvv,
				};
				if (zipCode) {
					this.paymentMethod.zipCode = zipCode;
				}
				break;
			}
			case PAYMENT_METHODS.directDebit: {
				const {
					iban,
					firstName,
					lastName,
				} = paymentMethod;
				this.paymentMethod = {
					iban,
					firstName,
					lastName,
				};
				break;
			}
			case PAYMENT_METHODS.businessAccount: {
				const {
					iban,
					companyName,
				} = paymentMethod;
				this.paymentMethod = {
					iban,
					companyName
				};
				break;
			}
			default:
				break;
		}
	}

	setPlanName(planName) {
		this.planName = planName;
	}

	setLegalEntities(legalEntities) {
		const {
			legalEntity,
			paymentServiceProvider
		} = legalEntities;

		this.legalEntities = {
			legalEntity, paymentServiceProvider
		};
	}


	setCustomerReference(customerReference) {
		this.customerReference = customerReference;
	}

	setBillingDetails(
		{
			email = '', company = '', state = '', city = '', zip = '',
			addressLine1 = '', addressLine2 = '', phone = '', vatNumber = '',
			firstName = '', lastName = '', invoiceInfo = '',
			country, regionId, regionName
		}
	) {
		const mightHaveRegionSetting = country in countryRegions;
		if (!mightHaveRegionSetting) {
			regionId = null;
			regionName = null;
		}
		this.billingDetails = {
			email, company, country, state, city, zip,
			addressLine1, addressLine2,
			phone, vatNumber, firstName, lastName, invoiceInfo,
			regionId, regionName,
		};
	}

	setVatNumber(vatNumber) {
		this.billingDetails.vatNumber = vatNumber;
	}

	setAvailablePaymentMethods({ creditCard, directDebit, businessAccount, suPayout, suPaymentMethods, suBusinessAccount }) {
		this.availablePaymentMethods = { creditCard, directDebit, businessAccount, suPayout, suPaymentMethods, suBusinessAccount };
	}

	setIsEdited(isEdited = false) {
		this.isEdited = isEdited;
	}

	// UTILS AND MAPPERS //
	async preparePayoutPM() {
		this.checkPaymentMethod(this.paymentMethodType);

		return {
			type: PAYMENT_METHODS.suPayout,
			suPayout: {
				merchantCode: this.customerReference,
			}
		};
	}

	async prepareSuPaymentMethodPM() {
		this.checkPaymentMethod(this.paymentMethodType);

		return {
			type: PAYMENT_METHODS.suPaymentMethods,
			suPaymentMethods: {
				merchantCode: this.customerReference,
			}
		};
	}

	async prepareSuBusinessAccountPM() {
		this.checkPaymentMethod(this.paymentMethodType);

		return {
			type: PAYMENT_METHODS.suBusinessAccount,
			suBusinessAccount: {
				merchantCode: this.customerReference,
			}
		};
	}

	get isPersistedPaymentMethodAvailable() {
		const type = this.persistedPaymentMethodType;
		if (type == null) {
			return false;
		}

		if (isPaymentMethodAvailable(this.availablePaymentMethods, type)) {
			return true;
		}

		// When a payment method is at active limit, it is dropped from `available` (cannot add another).
		// Merged availability therefore stays false for new selection — e.g. direct debit after a mandate exists.
		// Reuse the active row here for persisted preview only.
		return this._hasActivePaymentMethodForEmbedderType(type);
	}

	get availablePaymentMethodsForEdit() {
		const paymentMethodsForEdit = { ...this.availablePaymentMethods };

		for (const type of TOKENIZATION_PAYMENT_METHODS) {
			if (!paymentMethodsForEdit[type] && this._hasActivePaymentMethodForEmbedderType(type)) {
				paymentMethodsForEdit[type] = true;
			}
		}

		return paymentMethodsForEdit;
	}

	_hasActivePaymentMethodForEmbedderType(type) {
		if (!this.embedderAvailablePaymentMethods[type]) {
			return false;
		}

		return this.merchantWalletActivePaymentMethods.some(
			(paymentMethod) => paymentMethod.type === type,
		);
	}

	checkPaymentMethod(paymentMethodType) {
		if (!paymentMethodType) {
			throw new InternalError('No payment method provided');
		}

		switch (paymentMethodType) {
			case PAYMENT_METHODS.creditCard:
			case PAYMENT_METHODS.directDebit:
			case PAYMENT_METHODS.businessAccount:
			case PAYMENT_METHODS.suPayout:
			case PAYMENT_METHODS.suPaymentMethods:
			case PAYMENT_METHODS.suBusinessAccount:
				return true;
			default:
				throw new InternalError('Unknown payment method type', { paymentMethodType });
		}
	}

	async _setDefaultMerchantWalletPaymentMethod(paymentMethodType) {
		let activePaymentMethods = this.merchantWalletActivePaymentMethods;

		if (!activePaymentMethods.length) {
			const available = await this.merchantWalletService.getAvailablePaymentMethods();
			activePaymentMethods = available?.active || [];
			this.merchantWalletActivePaymentMethods = activePaymentMethods;
		}

		const activePaymentMethod = activePaymentMethods.find(
			(paymentMethod) => paymentMethod.type === paymentMethodType,
		);
		if (!activePaymentMethod?.id) {
			throw new InternalError('Payment method is not available for update', { paymentMethodType });
		}

		await this.merchantWalletService.patchPaymentMethod({
			paymentMethodId: activePaymentMethod.id,
			default: true,
		});
	}

	async _upsertMerchantWalletCustomer() {
		// TODO better solution for strict types
		const billingDetails = { ...this.billingDetails };
		setNullIfNaN(billingDetails, 'regionId');

		const customer = await this.merchantWalletService.upsertCustomer({
			email: billingDetails.email,
			phoneNumber: billingDetails.phone,
			billingAddress: billingDetails,
		});

		return customer;
	}

	async _prepareTokenization({ redirectUrl }) {
		const tokenization = await this.merchantWalletService.createTokenization({ redirectUrl });
		this.tokenizationId = tokenization.id;

		try {
			trace(TRACING_EVENTS.SCA_VERIFICATION_STARTED);
			const mandate = await this.getUserMandate();
			const checkoutType = this.paymentMethodType === PAYMENT_METHODS.businessAccount
				? PAYMENT_METHODS.directDebit
				: this.paymentMethodType;

			return await this.sumupService.processCheckout({
				checkoutId: tokenization.checkoutId,
				mandate,
				...this.paymentMethod,
				type: checkoutType,
			});
		} catch (err) {
			this.tokenizationId = null;
			throw err;
		}
	}

	async _confirmMerchantWalletTokenization({ checkoutId }) {
		let result;
		try {
			result = await this.merchantWalletService.confirmTokenization({
				tokenizationId: this.tokenizationId,
				paymentMethod: this._buildConfirmTokenizationPaymentMethod(),
			});
		} catch (err) {
			trace(TRACING_EVENTS.PAYMENT_METHOD_VERIFICATION_FAILED, { reason: err.message });
			throw err;
		} finally {
			this.tokenizationId = null;
		}

		if (result.tokenization.status !== 'successful') {
			trace(TRACING_EVENTS.PAYMENT_METHOD_VERIFICATION_FAILED, { reason: 'Payment method is not valid' });
			throw new PaymentMethodVerificationError('Payment method is not valid', { checkoutId });
		}

		trace(TRACING_EVENTS.PAYMENT_METHOD_VERIFICATION_COMPLETE);

		const { type, creditCard, directDebit, suPayout, suPaymentMethods, suBusinessAccount } = result.customer.paymentMethod;

		return {
			type,
			[type]: creditCard || directDebit || suPayout || suPaymentMethods || suBusinessAccount,
		};
	}

	_buildConfirmTokenizationPaymentMethod() {
		const isCreditCard = this.paymentMethodType === PAYMENT_METHODS.creditCard;
		const isDirectDebit = this.paymentMethodType === PAYMENT_METHODS.directDebit
			|| this.paymentMethodType === PAYMENT_METHODS.businessAccount;

		if (isCreditCard) {
			const { expiryMonth, expiryYear, cardholderName } = this.paymentMethod;
			return {
				type: PAYMENT_METHODS.creditCard,
				expiry: `${expiryMonth}/${expiryYear}`,
				cardholderName,
				setAsDefault: true,
			};
		}

		if (isDirectDebit) {
			const { iban } = this.paymentMethod;
			return {
				type: PAYMENT_METHODS.directDebit,
				iban,
				setAsDefault: true,
			};
		}

		return { setAsDefault: true };
	}

	_extractPaymentMethodDetails(paymentMethod) {
		const { paymentMethodType } = this;
		let paymentMethodDetails;

		if (paymentMethodType === PAYMENT_METHODS.creditCard) {
			paymentMethodDetails = {
				last4Digits: paymentMethod.creditCard.last4,
			};
			if (paymentMethod.creditCard.type) {
				paymentMethodDetails.cardType = paymentMethod.creditCard.type;
			}
		} else if (paymentMethodType === PAYMENT_METHODS.directDebit
			|| paymentMethodType === PAYMENT_METHODS.businessAccount) {
			paymentMethodDetails = {
				maskedIban: paymentMethod.directDebit?.maskedIban,
			};
		}

		return {
			...paymentMethodDetails,
			paymentMethodType: paymentMethodType === PAYMENT_METHODS.businessAccount
				? PAYMENT_METHODS.directDebit
				: paymentMethodType,
		};
	}

	_extractChargeDetails(charge = {}) {
		return {
			amount: charge.amountCents,
			currency: charge.currency,
			renewalDate: charge.endDate,
			planId: charge.planId
		};
	}
}

export default Customer;
